import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import Wordmark from "./Wordmark";
import { scrollToId, lockScroll } from "../lib/scroll";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";
import { EASE } from "./Reveal";

const LINKS = [
  { label: "Shop", id: "shop" },
  { label: "Our Story", id: "story" },
  { label: "Journal", id: "journal" },
];

export default function Nav({ ready }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const { count, openCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    lockScroll(menuOpen);
    return () => lockScroll(false);
  }, [menuOpen]);

  const go = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 750);
    } else {
      setTimeout(() => scrollToId(id), menuOpen ? 350 : 0);
    }
  };

  const openAuth = (tab) => {
    setMenuOpen(false);
    setAuthTab(tab);
    setAuthOpen(true);
  };

  const goAccount = () => {
    setMenuOpen(false);
    navigate("/account");
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-[60] bg-[#FDFCF9] transition-shadow duration-700 ${
          scrolled ? "shadow-[0_1px_0_rgba(20,16,12,0.08)]" : ""
        }`}
        style={{ opacity: 1 }}
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 1, delay: 0.15, ease: EASE }}
      >
        <div className="mx-auto max-w-[1500px] pl-6 md:pl-10 pr-6 md:pr-8 py-4 md:py-5 flex items-center justify-between">
          <button
            data-testid="nav-logo-button"
            onClick={() => {
              if (location.pathname !== "/") navigate("/");
              else window.scrollTo({ top: 0 });
            }}
            className="focus:outline-none"
            aria-label="Vivoré home"
          >
            <Wordmark tone="ink" className="h-[30px] md:h-[34px]" />
          </button>

          <nav className="hidden md:flex items-center gap-11">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-sans text-[13px] text-ink/75 hover:text-ink transition-colors duration-500 hover-line"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <button
              data-testid={user ? "nav-account" : "nav-sign-in"}
              onClick={() => (user ? navigate("/account") : openAuth("login"))}
              className="hidden md:block font-sans text-[12px] text-ink border border-ink/40 rounded-full px-5 py-[7px] hover:bg-ink hover:text-ivory transition-all duration-500"
            >
              {user ? "Account" : "Sign in"}
            </button>
            <button
              data-testid="cart-button"
              onClick={openCart}
              className="relative p-2 text-ink/80 hover:text-ink transition-colors duration-300"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    data-testid="cart-count-badge"
                    key="badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute top-0 right-0 w-[15px] h-[15px] rounded-full bg-lav text-white text-[9px] flex items-center justify-center"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              data-testid="mobile-menu-button"
              className="p-2 text-ink"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-ink text-ivory flex flex-col"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Wordmark tone="cream" className="h-8" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="p-2 -mr-2"
                aria-label="Close menu"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <div key={l.id} className="overflow-hidden">
                  <motion.button
                    data-testid={`mobile-nav-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="font-display text-5xl text-ivory py-2 block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15 + i * 0.08, ease: EASE }}
                  >
                    {l.label}
                  </motion.button>
                </div>
              ))}
              <div className="overflow-hidden mt-6">
                <motion.button
                  data-testid={user ? "mobile-nav-account" : "mobile-nav-signin"}
                  onClick={() => (user ? goAccount() : openAuth("login"))}
                  className="font-sans text-sm uppercase tracking-widest2 text-ivory/60 py-3 block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
                >
                  {user ? "Account" : "Sign in"}
                </motion.button>
              </div>
            </div>
            <div className="px-8 pb-10">
              <p className="eyebrow !text-ivory/50">Luxe Beauty, Timeless Grace</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialTab={authTab} />
    </>
  );
}
